import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/apiService';
import { annotateProjectsWithStorage, indexStorageByObservationId } from '../../utils/projectStorage';
import { formatSize } from '../../utils/fileUtils';

// Import the view components
import ProjectManagementView from './views/ProjectManagementView';
import ProjectOverviewView from './views/ProjectOverviewView';

const ProjectDashboardSimple = ({ mode = 'user' }) => {
  const { user } = useAuth();
  const [tab, setTab] = useState(0);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectLimit, setProjectLimit] = useState(null);
  const [totalStorageBytes, setTotalStorageBytes] = useState(null);
  const [storageByObservation, setStorageByObservation] = useState({});

  const isAdmin = mode === 'admin' || user?.role === 'server_admin' || user?.role === 'admin' || user?.role === 'instructor';

  useEffect(() => {
    loadProjects();
    apiService.getMyProjectLimit()
      .then((res) => setProjectLimit(res?.data || res))
      .catch(() => setProjectLimit(null));
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAllProjects();
      const projectsData = Array.isArray(response) ? response : [];

      // Summary counts come straight from the list response (observation_stats),
      // so we no longer fetch every observation for every project up front — the
      // old N+1 that made this page slow for admins who see many projects (#1504).
      // Each project's full observation list is loaded lazily when its row is
      // expanded (see ProjectOverviewView).
      const projectsWithStats = projectsData.map((project) => {
        const s = project.observation_stats || {};
        return {
          ...project,
          storage_bytes: 0, // filled in by the background storage fetch below
          stats: {
            total: s.total || 0,
            pending: s.pending || 0,
            running: s.running || 0,
            completed: s.completed || 0,
            failed: s.failed || 0,
          },
        };
      });

      // Render the list immediately with the server-provided counts. Storage usage
      // is a heavier aggregation that can be slow (and may even time out) for admins
      // who see every project, so fetch it in the BACKGROUND and fill it in without
      // blocking the list (#1504). Previously setProjects sat inside the storage
      // try/catch, so the list waited out the full 10s storage timeout before
      // appearing.
      setProjects(projectsWithStats);

      loadStorageUsage(projectsWithStats);
    } catch (err) {
      console.error('Failed to load projects:', err);
    } finally {
      setLoading(false);
    }
  };

  // Annotate the already-rendered projects with S3 storage usage, and build the
  // per-observation storage map for the expanded target/observation rows. Runs
  // out of the critical path; a failure (incl. timeout) leaves the list intact.
  const loadStorageUsage = async (baseProjects) => {
    try {
      const [projectStorageRes, obsStorageRes] = await Promise.all([
        apiService.getFileStorageByProject(),
        apiService.getFileStorageByObservation(),
      ]);
      const byProjectGroups = projectStorageRes?.data?.groups || [];
      const { projects: annotated, totalStorageBytes: total } =
        annotateProjectsWithStorage(baseProjects, byProjectGroups);
      setProjects(annotated);
      setTotalStorageBytes(total);
      setStorageByObservation(indexStorageByObservationId(obsStorageRes?.data?.groups || []));
    } catch (storageErr) {
      console.error('Failed to load project storage usage:', storageErr);
      // Storage unavailable — the list already rendered (storage_bytes: 0); just
      // omit the header total.
      setTotalStorageBytes(null);
      setStorageByObservation({});
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}><Typography>Loading...</Typography></Box>;
  }

  const title = mode === 'admin' ? 'Project Management' : 'My Projects';

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4">
        {title}
      </Typography>
      {mode !== 'admin' && (
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {projects.length} Project{projects.length === 1 ? '' : 's'}
          {totalStorageBytes != null ? ` · ${formatSize(totalStorageBytes)}` : ''}
          {projectLimit?.display_name
            ? `; Subscription "${projectLimit.display_name}" limit is ${projectLimit.max_projects == null ? 'unlimited' : projectLimit.max_projects}`
            : ''}
        </Typography>
      )}

      <Paper sx={{ mb: 3, mt: 2 }}>
        <Tabs value={tab} onChange={(e, val) => setTab(val)}>
          <Tab label="Overview" />
          <Tab label="Management" />
        </Tabs>
      </Paper>

      <Box sx={{ mt: 2 }}>
        {tab === 0 && (
          <ProjectOverviewView projects={projects} onReload={loadProjects} isAdmin={isAdmin} storageByObservation={storageByObservation} />
        )}
        {tab === 1 && (
          <ProjectManagementView projects={projects} onReload={loadProjects} isAdmin={isAdmin} />
        )}
      </Box>
    </Box>
  );
};

export default ProjectDashboardSimple;